
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useEffect, useMemo } from 'react';
import CountryIcon from '../../../components/core/CountryIcon';
import { GamesListMemo } from '../../../components/odds/GamesList';
import View from '../../../components/layout/View';
import { useCommonContext } from '../../../context/common.context';
import { useLiveContext } from '../../../context/live.context';
import { withPageSession } from '../../../lib/session';
import Image from 'next/legacy/image';
import { getFavoriteLeagueImagePath } from '../../../util/cards';
import { BackButton } from '../../../components/core/Button';
import { Game, GameMode, GameType } from '@arland-bmnext/api-data';
import { CmsPageIdentifier, getCmsPageData, useSportsBettingSettings } from '../../../lib/content';
import { PageFilterType } from '../../../filter/types';
import { marketTypeSpecifications, outrightDefaultMarketTypeId } from '../../../util/market-type-specifications';
import Betslip from '../../../components/odds/betslip/Betslip';
import OpenBetsWidget from '../../../components/widgets/OpenBetsWidget';
import SportLeagueBrowser from '../../../components/odds/slb/SportLeagueBrowser';
import { useRouter } from 'next/router';
import { getLeagueById, getLeagueGames } from '../../../lib/odds';
import useCurrentLanguage from '../../../hooks/useCurrentLanguage';
import { addUserFavoriteLeague, isAnonymousUser, removeUserFavoriteLeague, useUser, useUserFavoriteLeagues } from '../../../lib/user';
import { FavoriteLeagueDTO } from '@arland-bmnext/webapps-api-data';
import { faChevronRight, faStar } from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useSportradarWidget from '../../../hooks/useSportradarWidget';
import { buildEventUrl } from '../../../util/url';
import { faTrophy } from '@fortawesome/pro-light-svg-icons';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { isNullOrEmpty } from '../../../util/string';
import Handlebars from 'handlebars/dist/cjs/handlebars';
import useSportradarWidgetSettings from '../../../hooks/useSportradarWidgetSettings';
import useTranslation from 'next-translate/useTranslation';
import dayjs from 'dayjs';
const startDate = dayjs(new Date()).format('YYYY-MM-DD');
const endDate = dayjs(new Date()).add(1000, 'years').format('YYYY-MM-DD');
const marketTypeIds = [
    ...marketTypeSpecifications.reduce((acc, x) => acc.concat(x.marketTypeIds), []),
    outrightDefaultMarketTypeId
];
const buildMetaTitle = (frontendSettings, leagueGames, league, metaTitle): string => {
    const fallback = frontendSettings.brandName || '';
    if (leagueGames == null || league == null)
        return fallback;
    const templateProps = {
        brand: { name: frontendSettings.brandName },
        league: { name: league.name },
        sport: { name: leagueGames.sportName },
        category: { name: leagueGames.gameCategoryName }
    };
    if (metaTitle) {
        const template = !isNullOrEmpty(metaTitle) ? Handlebars.compile(metaTitle) : null;
        const res = !isNullOrEmpty(template) ? template(templateProps) : null;
        if (res && !res.includes('{'))
            return res;
        return fallback;
    }
    if (league) {
        return `${league.name} | ${frontendSettings.brandName}`;
    }
    return fallback;
};
const buildMetaDescription = (frontendSettings, leagueGames, league, metaDescription): string => {
    const fallback = frontendSettings.brandName || '';
    if (leagueGames == null || league == null)
        return fallback;
    const templateProps = {
        brand: { name: frontendSettings.brandName },
        league: { name: league.name },
        sport: { name: leagueGames.sportName },
        category: { name: leagueGames.gameCategoryName }
    };
    if (metaDescription) {
        const template = !isNullOrEmpty(metaDescription) ? Handlebars.compile(metaDescription) : null;
        const res = !isNullOrEmpty(template) ? template(templateProps) : null;
        if (res && !res.includes('{'))
            return res;
        return fallback;
    }
    return fallback;
};
const buildJsonLD = (req, leagueGames) => {
    const data = [];
    const protocol = req.headers['x-forwarded-proto'];
    const baseUrl = protocol + '://' + req.headers.host;
    leagueGames?.items?.forEach((leagueGame) => {
        const item = {
            '@type': 'SportsEvent',
            '@context': 'http://schema.org',
            name: leagueGame.name,
            startDate: leagueGame.startDate,
            url: baseUrl + buildEventUrl(leagueGame.id, leagueGame.name, leagueGame.usesUsDisplay)
        };
        data.push(item);
    });
    return {
        '@graph': [data]
    };
};
const LeagueImageSkeleton = () => {
    return (<div className={`flex relative w-full md:w-[calc(100%-32px)] aspect-[16/9] max-h-[30vh] md:max-h-[300px] md:m-4 md:rounded-md md:overflow-hidden transition-all skeleton skeleton-content`}>
      <div className="flex items-start w-full px-4 py-6">
        <div className="flex space-x-2 items-center z-[1]">
          <BackButton color="text-gray-500"/>
          <div className="flex items-start flex-col">
            <div className="p-[2px] mb-1 skeleton-content h-3 w-32"></div>
            <div className="skeleton-content h-6 w-24"></div>
          </div>
        </div>
      </div>
    </div>);
};
const LeagueImage = ({ leagueId, leagueName, gameCategoryId, gameCategoryName, gameCategoryShortSign, sportName, sportShortSign, sportId }) => {
    const router = useRouter();
    const { countries } = useCommonContext();
    const { user } = useUser();
    const language = useCurrentLanguage();
    const { favoriteLeagueLimit } = useSportsBettingSettings();
    const { userFavoriteLeagues, mutateUserFavoriteLeagues } = useUserFavoriteLeagues(language?.id);
    const country = countries?.find((c) => c['isoAlpha3'] === gameCategoryShortSign);
    const countryCode = country ? country?.shortSign : gameCategoryShortSign != '' ? gameCategoryShortSign : null;
    const isFavoriteLeague = userFavoriteLeagues?.find((fav) => fav.id === leagueId) != null;
    const onAddFavorite = async () => {
        if (isAnonymousUser(user)) {
            return router.push('/account/login');
        }
        addUserFavoriteLeague(leagueId);
        const favs = [
            ...userFavoriteLeagues,
            { id: leagueId, name: leagueName, sportId, gameCategoryId } as FavoriteLeagueDTO
        ];
        mutateUserFavoriteLeagues([...favs], { revalidate: false });
    };
    const onRemoveFavorite = async () => {
        removeUserFavoriteLeague(leagueId);
        const favs = [...userFavoriteLeagues.filter((fav) => fav.id != leagueId)];
        mutateUserFavoriteLeagues([...favs], { revalidate: false });
    };
    return (<div className={`flex relative aspect-[16/9] w-full md:w-[calc(100%-32px)] max-h-[30vh] md:max-h-[300px] md:m-4 md:rounded-md md:overflow-hidden transition-all text-white`}>
      <div className="flex items-start w-full px-4 py-6">
        <Image src={getFavoriteLeagueImagePath(sportShortSign, leagueId)} alt={leagueName} layout="fill" objectFit="cover" priority/>
        <div className="absolute right-0 top-0 h-full w-[calc(50%+5px)] bg-gradient-to-t from-cardDividerFrom to-cardDividerTo card-image-clip"></div>
        <div className="absolute right-0 top-0 !h-full !w-[50%] overflow-hidden card-image-clip">
          <CountryIcon countryCode={countryCode} rounded={false}/>
        </div>
        <div className="absolute bottom-0 left-0 h-full w-full bg-gradient-radial-to-br from-cardFade"></div>
        <div className="flex space-x-2 justify-between w-full z-[1]">
          <div className="flex grow space-x-2 items-center">
            <BackButton color="text-white"/>
            <div className="flex grow items-start flex-col">
              <h2 className="text-xs font-semibold bg-gradient-to-tr from-cardDividerFrom to-cardDividerTo text-primaryContrast p-[2px] mb-1">
                {sportName} | {gameCategoryName}
              </h2>
              <h1 className="text-[28px] font-extrabold uppercase leading-7 w-[60%]">{leagueName}</h1>
            </div>
          </div>
          <div className="text-3xl cursor-pointer">
            {isFavoriteLeague ? (<FontAwesomeIcon icon={faStarSolid} className="text-yellow-400 drop-shadow-lg" onClick={onRemoveFavorite}/>) : (<>
                {userFavoriteLeagues?.length < favoriteLeagueLimit && (<FontAwesomeIcon icon={faStar} className="drop-shadow-lg" onClick={onAddFavorite}/>)}
              </>)}
          </div>
        </div>
      </div>
    </div>);
};
export const LeagueImageMemo = React.memo(LeagueImage);
const LeagueStatistics = ({ league, languageId }) => {
    const sportRadarWidget = useSportradarWidget();
    const { t } = useTranslation('bets');
    const [expanded, setExpanded] = useLocalStorage<boolean>('showLeagueStats', false);
    useEffect(() => {
        if (league?.externalId && expanded) {
            sportRadarWidget.addTournamentPreview('sir-tournament-preview', league.externalId);
        }
        else {
            sportRadarWidget.removeWidget('sir-tournament-preview');
        }
        return () => {
            sportRadarWidget.removeWidget('sir-tournament-preview');
        };
    }, [league?.externalId, expanded]);
    return (<div className="league-statistics mb-2 md:m-4">
      <div className="league-statistics-inner border-b-[1px] md:border-[1px] border-neutral md:rounded-md md:overflow-hidden">
        <div className={`flex justify-between items-center pl-3 md:pl-4 py-4 pr-4 bg-header text-headerContrast cursor-pointer hover:underline ${expanded ? 'border-b-[1px] border-neutral' : ''}`} onClick={() => setExpanded(!expanded)}>
          <div className="flex space-x-2 items-center">
            <FontAwesomeIcon icon={faTrophy} className="league-statistics-icon text-primary"/>
            <span className="league-statistics-label text-sm font-semibold">{t('LeaguePage.leagueStatistics')}</span>
          </div>
          <FontAwesomeIcon icon={faChevronRight} className={`${expanded ? 'rotate-90' : ''} transition-transform`}/>
        </div>
        {expanded && <div id="sir-tournament-preview"></div>}
      </div>
    </div>);
};
const LeaguePage = ({ league, leagueGames }) => {
    const liveContext = useLiveContext();
    const language = useCurrentLanguage();
    const sportRadarWidgetSettings = useSportradarWidgetSettings();
    const liveGames = useMemo(() => liveContext.liveGames?.filter((game) => game.leagueId === league?.id), [liveContext.liveGames]);
    const outrights = useMemo(() => leagueGames?.items?.filter((game: Game) => game.type === GameType.League), [leagueGames]);
    const prematchGames = useMemo(() => leagueGames?.items?.filter((game) => liveGames?.find((liveGame) => liveGame.id === game.id) == null && game.type != GameType.League), [leagueGames, liveGames]);
    return (<div className="flex flex-col">
      <LeagueImageMemo leagueId={league?.id} leagueName={leagueGames?.leagueName} gameCategoryId={leagueGames?.gameCategoryId} gameCategoryName={leagueGames?.gameCategoryName} gameCategoryShortSign={leagueGames?.gameCategoryShortSign} sportId={leagueGames?.sportId} sportName={leagueGames?.sportName} sportShortSign={leagueGames?.sportShortSign}/>

      {sportRadarWidgetSettings.tournamentPreviewEnabled && league && (<LeagueStatistics league={league} languageId={language?.id}/>)}

      <div className="md:!w-[calc(100%-32px)] md:m-4">
        <GamesListMemo prematchGames={prematchGames} liveGames={liveGames} outrights={outrights} sportName={leagueGames?.sportName} sportShortSign={leagueGames?.sportShortSign} leagueName={leagueGames?.leagueName} leagueUsesUsDisplay={league?.usesUsDisplay} wrapperClassName="md:space-y-4" gamesListClassName="md:rounded-md md:border-[1px] border-neutral"/>
      </div>
    </div>);
};
LeaguePage.getLayout = function getLayout(_t, _pageProps, page) {
    return (<View pageId="league-page" headerProps={{ addStakeSelector: true, hideStakeSelectorOnScroll: false, addBonusBanner: true }} leftWidgets={[<SportLeagueBrowser mode={'slb'} redirectToSearchPage/>]} rightWidgets={[<OpenBetsWidget orientation="vertical"/>, <Betslip mode="desktop" dropShadow/>]} addFooter={false}>
      {page}
    </View>);
};
const getServerSideProps = withPageSession(async ({ req, session, query }, _pageFilterResult, pageData) => {
    const leagueQuery: string = query.leagueId as string;
    const dashIndex = leagueQuery.indexOf('-');
    const parsedLeagueId = dashIndex > 0 ? parseInt(leagueQuery.substring(0, dashIndex)) : parseInt(leagueQuery);
    const league = await getLeagueById(req, session, parsedLeagueId);
    const games = await getLeagueGames(req, session, parsedLeagueId, startDate, endDate, marketTypeIds, GameMode.PreMatch, false, 0, 100);
    const { metaTitle, metaDescription } = getCmsPageData(pageData.cmsPage);
    const _metaTitle = buildMetaTitle(pageData.frontendSettings, games, league, metaTitle);
    const _metaDescription = buildMetaDescription(pageData.frontendSettings, games, league, metaDescription);
    const _jsonLD = buildJsonLD(req, games);
    return {
        props: {
            ...pageData,
            league: league,
            leagueGames: games,
            metaTitle: _metaTitle,
            metaDescription: _metaDescription,
            jsonLD: _jsonLD
        }
    };
}, CmsPageIdentifier.LeaguePage, [PageFilterType.PageRequiresAuthentication]);
export default LeaguePage;

    async function __Next_Translate__getServerSideProps__1970be5a0e1__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/sports/league/[leagueId]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1970be5a0e1__ as getServerSideProps }
  